import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { bookingApi, getTemplates } from '../../../utils/bookingApi';
import { formatDateTime } from '../../../utils/dates';
import {
  PageHeader, FilterChips, Chevron, Sheet, Picker, Skeleton, ErrorState, useToast,
} from '../../../components/ui';
import MembershipEditor from './MembershipEditor';
import {
  statusFor, STATUS_CHIPS, formatDate, money, slotLabel, parentNames, DAY_LONG,
} from './membershipHelpers';
import '../booking-shared.css';
import './AdminMemberships.css';
import { useFeature } from '../../../hooks/useFeature';
import { useCardPayments } from '../../../hooks/useCardPayments';
import { useAuth } from '../../../contexts/AuthContext';
import { NOTE_ISSUES } from '../../../components/payments/MembershipPaymentWarning';

const BG_LABEL = { VERIFIED: 'BG ok', PENDING: 'BG pending', EXPIRED: 'BG expired', INVALID: 'BG invalid' };

function Section({ title, count, open, onToggle, tone, children }) {
  const bodyId = useId();
  return (
    <section className={`ms-section${tone ? ` ms-section--${tone}` : ''}`} data-open={open}>
      <button type="button" className="ms-section__head" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
        <span className="ms-section__title">{title}</span>
        {count != null && <span className="ms-section__count">{count}</span>}
        <Chevron />
      </button>
      {open && <div id={bodyId} className="ms-section__body">{children}</div>}
    </section>
  );
}

// What needs doing for a membership, or null when it's fine.
function attentionFor(m) {
  if (m.status === 'CANCELLED') return null;
  // Paid outside the app: the club tracks its payments, so none show here
  if (m.paidOutside) {
    return m.status === 'SCHEDULED' && !m.scheduledNotifiedAt ? `Starts ${formatDate(m.startDate)}. Family not told yet.` : null;
  }
  // Behind on payments, as last checked against Stripe
  // Nothing owed yet (a first payment still to come): just the note
  if (NOTE_ISSUES.includes(m.paymentIssue)) return m.paymentIssueDetail;
  if (m.paymentIssue) {
    const owed = m.paymentIssueAmount > 0 ? `${money(m.paymentIssueAmount)} behind. ` : '';
    return `${owed}${m.paymentIssueDetail}`;
  }
  if (m.status === 'PENDING_PAYMENT') return 'Payment not set up yet.';
  if (m.needsPaymentMethod) return 'Card payment failed. They are asked for a new card when they log in.';
  if (m.gymnast?.bgNumberStatus === 'EXPIRED') {
    return m.scheduledCancelAt
      ? `British Gymnastics membership expired. Cancels on ${formatDate(m.scheduledCancelAt)}.`
      : 'British Gymnastics membership expired.';
  }
  // Set to cancel for a reason the app doesn't know (read back from Stripe)
  if (m.scheduledCancelAt && new Date(m.scheduledCancelAt) > new Date()) return `Cancels on ${formatDate(m.scheduledCancelAt)}.`;
  if (m.status === 'SCHEDULED' && !m.scheduledNotifiedAt) return `Starts ${formatDate(m.startDate)}. Family not told yet.`;
  return null;
}

const EMPTY_FORM = { monthlyAmount: '', startDate: '', templateIds: [], chargeFullMonth: false, paidOutside: false };

// /memberships/options in the shape of the member list the add search reads:
// gymnasts with their guardians' ids, and the guardians themselves. A gymnast
// with no guardians is an adult participant.
function membersFromOptions(gymnasts) {
  const guardians = new Map();
  const rows = gymnasts.map(g => {
    g.guardians.forEach(p => guardians.set(p.id, { id: p.id, firstName: p.firstName, lastName: p.lastName }));
    return {
      id: g.id, firstName: g.firstName, lastName: g.lastName, isGymnast: true,
      guardianIds: g.guardians.map(p => p.id), adultParticipant: g.guardians.length === 0,
    };
  });
  return [...guardians.values(), ...rows];
}

// Adding a membership: who, how much, from when, and their weekly slots
function AddMembershipSheet({ participants, slotsByDay, cardsOn, onCreated, onClose }) {
  const [pickId, setPickId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const pick = participants.find(p => p.id === pickId) || null;
  // People who already have a membership aren't offered
  const items = useMemo(() => participants.filter(p => !p.has).map(p => ({
    id: p.id, label: p.name, detail: `${p.parent}${p.bg ? ` · ${p.bg}` : ''}`, search: p.parent,
  })), [participants]);
  const dirty = !!pickId || form.monthlyAmount !== '' || form.startDate !== '' || form.templateIds.length > 0 || form.paidOutside || form.chargeFullMonth;

  const handleCreate = async () => {
    setFormError(null);
    if (!pick) { setFormError('Choose a participant first.'); return; }
    if (!form.startDate) { setFormError('Choose a start date.'); return; }
    if (form.templateIds.length === 0) { setFormError('Choose at least one standing slot.'); return; }
    const monthlyPence = Math.round(parseFloat(form.monthlyAmount) * 100);
    // Paid outside the app: the amount is for the club's reference, so 0 is fine
    const lowest = form.paidOutside ? 0 : 1;
    if (!Number.isFinite(monthlyPence) || monthlyPence < lowest) { setFormError('Enter a monthly amount.'); return; }
    setSubmitting(true);
    try {
      const payload = { gymnastId: pick.id, monthlyAmount: monthlyPence, startDate: form.startDate, templateIds: form.templateIds };
      if (form.paidOutside) payload.paidOutside = true;
      else if (form.chargeFullMonth) payload.firstMonthAmount = monthlyPence;
      const res = await bookingApi.createMembership(payload);
      await onCreated(pick, res.data?.membership);
    } catch (err) {
      setFormError(err.response?.data?.error || "Couldn't create the membership.");
      setSubmitting(false);
    }
  };

  const toggleTemplate = id => setForm(f => ({
    ...f,
    templateIds: f.templateIds.includes(id) ? f.templateIds.filter(t => t !== id) : [...f.templateIds, id],
  }));

  return (
    <Sheet
      title="Add a membership"
      wide
      onClose={onClose}
      dirty={dirty}
      onSubmit={handleCreate}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={submitting || (!cardsOn && !form.paidOutside)}>
            {submitting ? 'Creating…' : 'Create membership'}
          </button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="ms-form">
        <div className="bk-label">
          <label htmlFor="ms-add-participant">Participant</label>
          <Picker id="ms-add-participant" label="Participant" items={items} value={pickId} onChange={setPickId}
            placeholder="Search by name or parent…" noneText="No participants match." />
          {pick && <span className="ms-muted ms-small">Parent: {pick.parent}{pick.bg ? ` · ${pick.bg}` : ''}</span>}
        </div>

        <div className="ms-two">
          <label className="bk-label">
            Monthly amount (£)
            <input type="number" step="0.01" min={form.paidOutside ? '0' : '0.01'} className="bk-input" placeholder="40.00" inputMode="decimal"
              value={form.monthlyAmount} onChange={e => setForm(f => ({ ...f, monthlyAmount: e.target.value }))} required />
          </label>
          <label className="bk-label">
            Start date
            <input type="date" className="bk-input" value={form.startDate}
              onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} required />
          </label>
        </div>
        <div className="ms-field">
          <label className="ms-check">
            <input type="checkbox" checked={form.paidOutside} onChange={e => setForm(f => ({ ...f, paidOutside: e.target.checked }))} />
            Paid outside the app
          </label>
          <span className="ms-muted ms-small">For cash, standing order or anything else the club collects itself. The app won't take or track payments for this membership.</span>
        </div>
        {!form.paidOutside && (
          <label className="ms-check">
            <input type="checkbox" checked={form.chargeFullMonth} onChange={e => setForm(f => ({ ...f, chargeFullMonth: e.target.checked }))} />
            Charge a full first month, whatever the start date
          </label>
        )}

        <fieldset className="ms-slots">
          <legend className="ms-field">Standing slots</legend>
          {slotsByDay.length === 0 && <span className="ms-muted ms-small">No sessions set up yet.</span>}
          {slotsByDay.map(({ day, slots }) => (
            <div key={day} className="ms-slots__day">
              <span className="ms-slots__dayname">{DAY_LONG[day]}</span>
              {slots.map(t => (
                <label key={t.id} className="ms-check">
                  <input type="checkbox" checked={form.templateIds.includes(t.id)} onChange={() => toggleTemplate(t.id)} />
                  {slotLabel(t)}
                </label>
              ))}
            </div>
          ))}
        </fieldset>

        {formError && <p className="bk-form-error" role="alert">{formError}</p>}
        {!cardsOn && !form.paidOutside && (
          <p className="bk-muted" role="status">
            Memberships paid by card need card payments. <Link to="/booking/admin/card-payments">Set up card payments</Link> first, or tick Paid outside the app.
          </p>
        )}
      </div>
    </Sheet>
  );
}

export default function AdminMemberships() {
  const bgOn = useFeature('bgNumbers');
  const cardsOn = useCardPayments();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('membership');
  const [memberships, setMemberships] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [open, setOpen] = useState({ attention: true, list: true });
  const [adding, setAdding] = useState(false);
  const [chip, setChip] = useState('');
  // ?search= comes from older links to this page
  const [query, setQuery] = useState(() => searchParams.get('search') || '');
  const [openRow, setOpenRow] = useState(focusId);
  const [notifying, setNotifying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  // Changes need Manage access to payments. Adding a membership searches the
  // club's participants and picks standing slots. Staff who can see members
  // and sessions use those lists (with BG numbers); anyone else who manages
  // payments (Finance) gets names and slots only from /memberships/options.
  const { can } = useAuth();
  const managesPayments = can('payments', 'MANAGE');
  const seesMembers = can('members');
  const seesSessions = can('sessions');
  const useOptions = managesPayments && !(seesMembers && seesSessions);
  const focusHandled = useRef(false);

  const load = useCallback(async () => {
    if (useOptions) {
      const [ms, opts] = await Promise.all([bookingApi.getMemberships(), bookingApi.getMembershipOptions()]);
      setMemberships(ms.data);
      setTemplates(opts.data?.slots || []);
      setMembers(membersFromOptions(opts.data?.gymnasts || []));
      setLoaded(true);
      return;
    }
    const [ms, ts] = await Promise.all([bookingApi.getMemberships(), seesSessions ? getTemplates() : Promise.resolve({ data: [] })]);
    setMemberships(ms.data);
    setTemplates(ts.data);
    setLoaded(true);
  }, [seesSessions, useOptions]);

  const firstLoad = useCallback(() => {
    setLoadError(false);
    load().catch(() => setLoadError(true));
  }, [load]);

  useEffect(() => {
    firstLoad();
    if (seesMembers && !useOptions) bookingApi.getMembers().then(res => setMembers(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Arriving from a member page: show that membership, expanded.
  useEffect(() => {
    if (!loaded || !focusId || focusHandled.current) return;
    focusHandled.current = true;
    const target = memberships.find(m => m.id === focusId);
    if (!target) return;
    setQuery('');
    setChip(target.status === 'CANCELLED' ? 'CANCELLED' : '');
    setOpen(o => ({ ...o, list: true }));
    setOpenRow(focusId);
    requestAnimationFrame(() => {
      document.getElementById(`ms-row-${focusId}`)?.scrollIntoView?.({ block: 'start' });
    });
  }, [loaded, focusId, memberships]);

  const toggle = key => setOpen(o => ({ ...o, [key]: !o[key] }));

  const behind = memberships.filter(m => m.paymentIssue && !NOTE_ISSUES.includes(m.paymentIssue) && m.status !== 'CANCELLED').length;
  const lastChecked = memberships.map(m => m.paymentCheckedAt).filter(Boolean).sort().pop();
  const checkPayments = async () => {
    setChecking(true);
    setError(null);
    try {
      const res = await bookingApi.checkMembershipPayments();
      const n = res.data.delinquent;
      toast(n ? `${n} membership${n === 1 ? ' is' : 's are'} behind on payments.` : 'Everyone is up to date with payments.');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't check payments. Try again.");
    } finally {
      setChecking(false);
    }
  };

  const attention = memberships
    .map(m => ({ m, why: attentionFor(m) }))
    .filter(a => a.why);
  const unnotified = memberships.filter(m => m.status === 'SCHEDULED' && !m.scheduledNotifiedAt);

  const filtered = memberships.filter(m => {
    const s = statusFor(m).key;
    if (chip ? s !== chip : s === 'CANCELLED') return false;
    if (query) {
      const haystack = `${m.gymnast.firstName} ${m.gymnast.lastName} ${parentNames(m.gymnast)}`.toLowerCase();
      if (!haystack.includes(query.trim().toLowerCase())) return false;
    }
    return true;
  });
  const liveCount = memberships.filter(m => m.status !== 'CANCELLED').length;

  const openMembership = id => {
    setQuery('');
    setChip('');
    setOpen(o => ({ ...o, list: true }));
    setOpenRow(id);
    requestAnimationFrame(() => {
      document.getElementById(`ms-row-${id}`)?.scrollIntoView?.({ block: 'nearest' });
    });
  };

  const act = async (fn, success) => {
    setError(null);
    try {
      await fn();
      toast(success);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Try again.');
    }
  };

  const notifyAll = async () => {
    setNotifying(true);
    setError(null);
    try {
      const res = await bookingApi.notifyScheduledMemberships();
      const { sent, skipped } = res.data;
      toast(`Told ${sent} ${sent === 1 ? 'family' : 'families'}${skipped ? `, ${skipped} skipped (no parent email)` : ''}.`);
      await load();
    } catch {
      setError("Couldn't send the notifications. Try again.");
    } finally {
      setNotifying(false);
    }
  };

  // Participants for the add search, with their parent's name. Gymnasts
  // whose accounts are all archived drop out with them.
  const participants = useMemo(() => {
    const users = new Map(members.filter(u => !u.isGymnast).map(u => [u.id, u]));
    const withMembership = new Set(memberships.filter(m => m.status !== 'CANCELLED').map(m => m.gymnastId));
    return members
      .filter(g => g.isGymnast)
      .map(g => {
        const parents = (g.guardianIds || []).map(id => users.get(id)).filter(Boolean);
        const self = g.userId ? users.get(g.userId) : (g.adultParticipant ? g : null);
        if (parents.length === 0 && !self) return null;
        return {
          id: g.id,
          name: `${g.firstName} ${g.lastName}`,
          parent: parents.length ? parents.map(p => `${p.firstName} ${p.lastName}`).join(', ') : 'Adult participant',
          bg: bgOn && g.bgNumberStatus !== undefined ? (BG_LABEL[g.bgNumberStatus] || 'No BG number') : '',
          has: withMembership.has(g.id),
        };
      })
      .filter(Boolean);
  }, [members, memberships, bgOn]);

  const slotsByDay = useMemo(() => {
    const active = templates.filter(t => t.isActive);
    return [1, 2, 3, 4, 5, 6, 0]
      .map(day => ({
        day,
        slots: active.filter(t => t.dayOfWeek === day).sort((a, b) => a.startTime.localeCompare(b.startTime)),
      }))
      .filter(d => d.slots.length);
  }, [templates]);

  const header = (
    <PageHeader
      back={{ to: '/admin-hub', label: 'Admin' }}
      title="Memberships"
      action={managesPayments && loaded ? { label: 'Add membership', short: 'Add', onClick: () => setAdding(true) } : undefined}
    />
  );

  if (!loaded) {
    return (
      <div className="bk-page bk-page--wide ms-page">
        {header}
        {loadError ? <ErrorState title="Couldn't load memberships" onRetry={firstLoad} /> : <Skeleton rows={5} label="Loading memberships…" />}
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--wide ms-page">
      {header}

      <div className="ms-payments-check">
        <span>
          {behind > 0 ? <strong>{behind} behind on payments. </strong> : 'Payments up to date. '}
          {lastChecked ? `Checked with Stripe ${formatDateTime(lastChecked)}.` : 'Not checked with Stripe yet.'}
        </span>
        {managesPayments && (
          <button type="button" className="bk-btn bk-btn--sm" disabled={checking} onClick={checkPayments}>
            {checking ? 'Checking…' : 'Check payments now'}
          </button>
        )}
      </div>

      {error && <p className="bk-form-error" role="alert">{error}</p>}

      {attention.length > 0 && (
        <Section title="Needs attention" count={attention.length} tone="attention" open={open.attention} onToggle={() => toggle('attention')}>
          {attention.map(({ m, why }) => {
            const s = statusFor(m);
            return (
              <div key={m.id} className="ms-attn">
                <div className="ms-attn__top">
                  <span className="ms-attn__name">{m.gymnast.firstName} {m.gymnast.lastName}</span>
                  <span className={`bk-pill bk-pill--${s.tone}`}>{s.label}</span>
                </div>
                <span className="ms-attn__why">{why}</span>
                <div className="bk-row-actions">
                  {managesPayments && m.status === 'PENDING_PAYMENT' && !m.paidOutside && (
                    <>
                      <button type="button" className="bk-btn bk-btn--sm" onClick={() => act(() => bookingApi.sendMembershipReminder(m.id), 'Reminder sent')}>Send reminder</button>
                      <button type="button" className="bk-btn bk-btn--sm" onClick={() => act(() => bookingApi.resetMembership(m.id), 'Payment setup reset')}>Reset payment setup</button>
                    </>
                  )}
                  {managesPayments && s.key === 'CANCELLING' && (
                    <button type="button" className="bk-btn bk-btn--sm" onClick={() => act(() => bookingApi.resumeMembership(m.id), 'Cancellation undone')}>Undo cancellation</button>
                  )}
                  <button type="button" className="bk-btn bk-btn--sm" onClick={() => openMembership(m.id)}>Open</button>
                </div>
              </div>
            );
          })}
          {managesPayments && unnotified.length > 0 && (
            <button type="button" className="bk-btn bk-btn--primary" disabled={notifying} onClick={notifyAll}>
              {notifying ? 'Sending…' : `Notify everyone starting soon (${unnotified.length})`}
            </button>
          )}
        </Section>
      )}

      <Section title="Memberships" count={liveCount} open={open.list} onToggle={() => toggle('list')}>
        <input
          type="search"
          className="bk-input ms-search"
          placeholder="Search participant or parent"
          aria-label="Search memberships"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <FilterChips label="Filter by status" chips={STATUS_CHIPS} value={chip} onChange={setChip} />
        <div className="ms-list">
          {filtered.length === 0 && <p className="ms-muted ms-empty">No memberships match.</p>}
          {filtered.map(m => {
            const s = statusFor(m);
            const isOpen = openRow === m.id;
            return (
              <div key={m.id} id={`ms-row-${m.id}`} className="ms-row" data-open={isOpen}>
                <button type="button" className="ms-row__head" aria-expanded={isOpen} onClick={() => setOpenRow(isOpen ? null : m.id)}>
                  <span className="ms-row__main">
                    <span className="ms-row__name">{m.gymnast.firstName} {m.gymnast.lastName}</span>
                    <span className="ms-row__sub">{parentNames(m.gymnast)}</span>
                    {m.paidOutside && <span className="ms-tag">Paid outside the app</span>}
                  </span>
                  <span className={`bk-pill bk-pill--${s.tone}`}>{s.label}</span>
                  <span className="ms-row__amt">{money(m.monthlyAmount)}</span>
                </button>
                {isOpen && (
                  <MembershipEditor
                    key={`${m.id}-${m.updatedAt}`}
                    membership={m}
                    templates={templates}
                    onChanged={load}
                    canManage={managesPayments}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {adding && (
        <AddMembershipSheet
          participants={participants}
          slotsByDay={slotsByDay}
          cardsOn={cardsOn}
          onClose={() => setAdding(false)}
          onCreated={async (pick, created) => {
            setAdding(false);
            toast(`Membership created for ${pick.name}`);
            await load().catch(() => setError("Created, but couldn't refresh the list. Reload the page."));
            if (created?.id) openMembership(created.id);
          }}
        />
      )}
    </div>
  );
}
